import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Plus,
  Trash2,
  Save,
  Pencil,
  X,
  RefreshCw,
  HardHat,
} from "lucide-react";
import { toast } from "sonner";

export default function CrewLeaders() {
  const nav = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", email: "", phone: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crew-leaders", {
        params: { include_inactive: includeInactive ? "true" : "false" },
      });
      setLeaders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/crew-leaders", {
        name: newName.trim(),
        email: newEmail.trim(),
        phone: newPhone.trim(),
      });
      toast.success(`Added ${newName.trim()}`);
      setNewName("");
      setNewEmail("");
      setNewPhone("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setEditDraft({ name: l.name, email: l.email || "", phone: l.phone || "" });
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/crew-leaders/${id}`, editDraft);
      toast.success("Updated");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const removeLeader = async (id, name) => {
    if (!window.confirm(`Remove crew leader "${name}"?\nExisting shipments keep the name as a label.`)) return;
    try {
      await api.delete(`/crew-leaders/${id}`);
      toast.success(`Removed ${name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Remove failed");
    }
  };

  const reactivate = async (id, name) => {
    try {
      await api.patch(`/crew-leaders/${id}`, { active: true });
      toast.success(`Reactivated ${name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <button
        onClick={() => nav(-1)}
        data-testid="back-btn"
        className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 mb-3"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">
        Admin / Settings
      </div>
      <h1 className="font-display text-3xl uppercase text-slate-900 tracking-tight flex items-center gap-2">
        <HardHat strokeWidth={2.5} className="h-7 w-7" />
        Crew Leaders
      </h1>
      <p className="text-sm text-slate-600 mt-1 mb-6">
        These names appear in the Assigned Crew Leader picker on every shipment.
      </p>

      {/* Add form */}
      <form
        onSubmit={onAdd}
        data-testid="add-leader-form"
        className="rg-card p-4 mb-6 space-y-3"
      >
        <div className="font-display uppercase text-sm tracking-wide text-slate-900">
          Add a crew leader
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="new-leader-name"
            placeholder="Name *"
            className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
          />
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            data-testid="new-leader-email"
            placeholder="Email (helps Assigned-to-me)"
            className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
          />
          <input
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            data-testid="new-leader-phone"
            placeholder="Phone (optional)"
            className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="add-leader-submit"
          className="rg-tap-xl w-full bg-slate-900 hover:bg-slate-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Plus strokeWidth={3} className="h-5 w-5" />
          {submitting ? "Adding…" : "Add crew leader"}
        </button>
      </form>

      {/* List */}
      <div className="rg-card">
        <div className="flex items-center justify-between p-4 border-b-2 border-slate-200">
          <h2 className="font-display uppercase text-base text-slate-900">
            All crew leaders ({leaders.length})
          </h2>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                data-testid="show-inactive"
                className="h-4 w-4"
              />
              Show inactive
            </label>
            <button
              onClick={load}
              data-testid="reload-leaders"
              className="rg-tap px-2 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-slate-500">Loading…</div>
        ) : leaders.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            No crew leaders yet. Add one above.
          </div>
        ) : (
          <ul data-testid="leaders-list">
            {leaders.map((l) => {
              const editing = editingId === l.id;
              return (
                <li
                  key={l.id}
                  data-testid={`leader-row-${l.id}`}
                  className={`p-4 border-b border-slate-100 ${l.active ? "" : "bg-slate-50 opacity-70"}`}
                >
                  {editing ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <input
                        value={editDraft.name}
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                      />
                      <input
                        value={editDraft.email}
                        onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })}
                        placeholder="Email"
                        className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                      />
                      <input
                        value={editDraft.phone}
                        onChange={(e) => setEditDraft({ ...editDraft, phone: e.target.value })}
                        placeholder="Phone"
                        className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                      />
                      <div className="sm:col-span-3 flex gap-2">
                        <button
                          onClick={() => saveEdit(l.id)}
                          data-testid={`save-edit-${l.id}`}
                          className="rg-tap flex-1 bg-slate-900 text-white font-display uppercase tracking-wide rounded-sm flex items-center justify-center gap-2"
                        >
                          <Save strokeWidth={3} className="h-4 w-4" /> Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="rg-tap px-4 border-2 border-slate-300 rounded-sm font-bold uppercase tracking-wide text-sm"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg uppercase text-slate-900 flex items-center gap-2">
                          {l.name}
                          {!l.active && (
                            <span className="rg-pill text-slate-500 bg-slate-200">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600">
                          {l.email || <span className="text-slate-400">No email</span>}
                          {l.phone ? ` · ${l.phone}` : ""}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {l.active ? (
                          <>
                            <button
                              onClick={() => startEdit(l)}
                              data-testid={`edit-${l.id}`}
                              className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white flex items-center gap-1 font-bold uppercase tracking-wide text-sm text-slate-700"
                            >
                              <Pencil className="h-4 w-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => removeLeader(l.id, l.name)}
                              data-testid={`remove-${l.id}`}
                              className="rg-tap px-3 border-2 border-rose-300 text-rose-700 hover:bg-rose-50 rounded-sm bg-white flex items-center gap-1 font-bold uppercase tracking-wide text-sm"
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => reactivate(l.id, l.name)}
                            data-testid={`reactivate-${l.id}`}
                            className="rg-tap px-3 border-2 border-green-600 text-green-700 hover:bg-green-50 rounded-sm bg-white flex items-center gap-1 font-bold uppercase tracking-wide text-sm"
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
